import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { invoiceAPI } from '../services/api';
import { downloadInvoicePDF, printInvoicePDF } from '../utils/pdfGenerator';
import './InvoiceList.css';

function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await invoiceAPI.getAll();
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      alert('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, invoiceNumber) => {
    if (window.confirm(`Are you sure you want to delete invoice "${invoiceNumber}"?`)) {
      try {
        await invoiceAPI.delete(id);
        setInvoices(invoices.filter(inv => inv._id !== id));
        alert('Invoice deleted successfully');
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Failed to delete invoice');
      }
    }
  };

  const handleDownloadPDF = async (invoice) => {
    await downloadInvoicePDF(invoice);
    // Log the download action
    try {
      await invoiceAPI.logDownload(invoice._id);
    } catch (error) {
      console.error('Error logging download:', error);
    }
  };

  const handlePrintPDF = async (invoice) => {
    await printInvoicePDF(invoice);
    // Log the print action
    try {
      await invoiceAPI.logPrint(invoice._id);
    } catch (error) {
      console.error('Error logging print:', error);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      paid: 'badge-paid',
      overdue: 'badge-overdue',
      cancelled: 'badge-cancelled'
    };
    return statusClasses[status] || 'badge-default';
  };

  const filteredInvoices = invoices
    .filter(invoice => {
      const matchesSearch =
        invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customerSnapshot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (invoice.customerSnapshot.company &&
          invoice.customerSnapshot.company.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    })
    .sort((a, b) => {
      // Sort by invoice number in descending order (newest on top)
      // Extract numeric part from invoice number (e.g., "INV-0001" -> 1)
      const getNumericPart = (invoiceNumber) => {
        const match = invoiceNumber.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
      };
      return getNumericPart(b.invoiceNumber) - getNumericPart(a.invoiceNumber);
    });

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  return (
    <div className="invoice-list">
      <div className="page-header">
        <h2>Invoices</h2>
        <Link to="/invoices/new" className="btn btn-primary">
          Create New Invoice
        </Link>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search by invoice #, customer name, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices found.</p>
          {searchTerm && <p>Try adjusting your search.</p>}
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Loads</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => (
              <tr key={invoice._id}>
                <td>
                  <Link to={`/invoices/edit/${invoice._id}`} className="link">
                    {invoice.invoiceNumber}
                  </Link>
                </td>
                <td>
                  {invoice.customerSnapshot.company || invoice.customerSnapshot.name}
                </td>
                <td>{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                <td>{invoice.loads.length}</td>
                <td>${invoice.total.toFixed(2)}</td>
                <td className="actions">
                  <button
                    onClick={() => handleDownloadPDF(invoice)}
                    className="btn btn-small btn-icon"
                    title="Download PDF"
                  >
                    📥
                  </button>
                  <button
                    onClick={() => handlePrintPDF(invoice)}
                    className="btn btn-small btn-icon"
                    title="Print"
                  >
                    🖨️
                  </button>
                  <Link
                    to={`/invoices/edit/${invoice._id}`}
                    className="btn btn-small btn-secondary"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(invoice._id, invoice.invoiceNumber)}
                    className="btn btn-small btn-danger"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default InvoiceList;
